import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Business as BusinessIcon,
  PersonAdd as PersonAddIcon,
  Login as LoginIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../context/AuthContext';
import { invitationsService } from '../services/firebase/invitations.service';
import { employeesService } from '../services/firebase/employees.service';
import { authService } from '../services/firebase/auth.service';
import { Invitation } from '../types/organization.types';
import InvitationSignupForm from '../components/auth/InvitationSignupForm';
import EmailVerificationRequired from '../components/auth/EmailVerificationRequired';

type PageState =
  | 'loading'
  | 'invalid'
  | 'expired'
  | 'already_accepted'
  | 'signup'
  | 'login_required'
  | 'ready_to_accept'
  | 'wrong_account'
  | 'verification_required'
  | 'accepting';

export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, emailVerified, loading: authLoading } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [employeeName, setEmployeeName] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [invitationAccepted, setInvitationAccepted] = useState<boolean>(false);

  // Fetch invitation data on mount
  useEffect(() => {
    async function fetchInvitation() {
      if (!token) {
        setPageState('invalid');
        setErrorMessage('Invalid invitation link.');
        return;
      }

      try {
        const result = await invitationsService.getInvitationByToken(token);

        if (!result) {
          setPageState('invalid');
          setErrorMessage('This invitation link is invalid or has been deleted.');
          return;
        }

        const { invitation: inv, organizationName: orgName } = result;
        setInvitation(inv);
        setOrganizationName(orgName);

        // Check invitation status
        if (inv.status === 'accepted') {
          setPageState('already_accepted');
          return;
        }

        if (inv.status === 'expired' || inv.status === 'cancelled') {
          setPageState('expired');
          return;
        }

        // Check if expired by date
        const now = new Date();
        const expiresAt = inv.expiresAt.toDate();
        if (now > expiresAt) {
          setPageState('expired');
          return;
        }

        // Get employee name from invitation (stored in token for public access)
        if (inv.invitationType === 'employee') {
          const invWithName = inv as Invitation & { employeeName?: string };
          if (invWithName.employeeName) {
            setEmployeeName(invWithName.employeeName);
          }
        }

        // Invitation is valid, determine next state based on auth
        determineStateBasedOnAuth(inv);
      } catch (error) {
        console.error('Error fetching invitation:', error);
        setPageState('invalid');
        setErrorMessage('Failed to load invitation. Please try again.');
      }
    }

    if (!authLoading) {
      fetchInvitation();
    }
  }, [token, authLoading]);

  // Re-evaluate state when user auth changes
  useEffect(() => {
    if (invitation && !authLoading) {
      determineStateBasedOnAuth(invitation);
    }
  }, [user, emailVerified, authLoading, invitation]);

  async function determineStateBasedOnAuth(inv: Invitation) {
    if (user) {
      // User is logged in
      if (!emailVerified) {
        setPageState('verification_required');
        return;
      }

      // Check if logged in with correct email
      if (user.email?.toLowerCase() !== inv.email.toLowerCase()) {
        setPageState('wrong_account');
        return;
      }

      // Ready to accept
      setPageState('ready_to_accept');
    } else {
      // User is not logged in
      // Check if email already has an account
      const emailExists = await authService.checkEmailExists(inv.email);
      if (emailExists) {
        setPageState('login_required');
      } else {
        setPageState('signup');
      }
    }
  }

  async function handleAcceptInvitation() {
    if (!invitation || !user) return;

    try {
      setPageState('accepting');

      // Accept the invitation
      await invitationsService.acceptInvitation(
        invitation.organizationId,
        invitation.id,
        user.uid
      );

      // Update user's organization and role
      if (invitation.invitationType === 'employee' && invitation.employeeId) {
        // Link employee to user account
        await employeesService.linkEmployeeToUser(
          invitation.organizationId,
          invitation.employeeId,
          user.uid
        );

        // Update user document with organization, role, and employee ID
        await authService.updateUserOrganizationWithEmployee(
          user.uid,
          invitation.organizationId,
          invitation.role,
          invitation.employeeId
        );

        enqueueSnackbar('Welcome! You can now view your schedules.', { variant: 'success' });
        navigate('/employee/dashboard');
      } else {
        // Staff invitation - update organization and role
        await authService.updateUserOrganizationWithEmployee(
          user.uid,
          invitation.organizationId,
          invitation.role
        );

        enqueueSnackbar(`Welcome to ${organizationName}!`, { variant: 'success' });
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      const message = error instanceof Error ? error.message : 'Failed to accept invitation';
      enqueueSnackbar(message, { variant: 'error' });
      setPageState('ready_to_accept');
    }
  }

  function handleSignupComplete() {
    // After signup, the user needs to verify their email
    // Directly set verification_required state instead of re-evaluating
    // (avoids race condition with auth state propagation)
    // Mark invitation as accepted (done during signup)
    setInvitationAccepted(true);
    setPageState('verification_required');
  }

  function handleVerificationComplete() {
    // For signup flow, invitation is already accepted during signup
    if (invitationAccepted) {
      if (invitation?.invitationType === 'employee') {
        enqueueSnackbar('Welcome! You can now view your schedules.', { variant: 'success' });
        navigate('/employee/dashboard');
      } else {
        enqueueSnackbar(`Welcome to ${organizationName}!`, { variant: 'success' });
        navigate('/dashboard');
      }
    } else {
      // For login flow, invitation not yet accepted, show ready_to_accept
      setPageState('ready_to_accept');
    }
  }

  // Render loading state
  if (pageState === 'loading' || authLoading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Render error states
  if (pageState === 'invalid' || pageState === 'expired' || pageState === 'already_accepted') {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: 4,
          }}
        >
          <Paper sx={{ p: 4, width: '100%', textAlign: 'center' }}>
            <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              {pageState === 'invalid' && 'Invalid Invitation'}
              {pageState === 'expired' && 'Invitation Expired'}
              {pageState === 'already_accepted' && 'Already Accepted'}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {pageState === 'invalid' && (errorMessage || 'This invitation link is not valid.')}
              {pageState === 'expired' &&
                'This invitation has expired. Please ask for a new invitation.'}
              {pageState === 'already_accepted' &&
                'This invitation has already been accepted.'}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/auth')}>
              Go to Sign In
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Render main invitation page
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <Paper sx={{ p: 4, width: '100%' }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <BusinessIcon sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Typography variant="h5" gutterBottom>
              You're Invited!
            </Typography>
            <Typography color="text.secondary">
              {invitation?.invitationType === 'employee'
                ? `Join ${organizationName} to view your work schedule`
                : `Join ${organizationName} as ${invitation?.role}`}
            </Typography>
            {employeeName && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Welcome, {employeeName}!
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Verification Required */}
          {pageState === 'verification_required' && (
            <EmailVerificationRequired
              email={user?.email || invitation?.email || ''}
              onVerified={handleVerificationComplete}
            />
          )}

          {/* Wrong Account */}
          {pageState === 'wrong_account' && (
            <Box sx={{ textAlign: 'center' }}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                You're signed in as <strong>{user?.email}</strong>, but this invitation is for{' '}
                <strong>{invitation?.email}</strong>.
              </Alert>
              <Button
                variant="contained"
                onClick={async () => {
                  await authService.logout();
                  if (invitation) {
                    determineStateBasedOnAuth(invitation);
                  }
                }}
              >
                Sign Out & Continue
              </Button>
            </Box>
          )}

          {/* Ready to Accept */}
          {pageState === 'ready_to_accept' && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                You're about to join <strong>{organizationName}</strong>
                {invitation?.invitationType === 'staff' && (
                  <> as a <strong>{invitation.role}</strong></>
                )}
                .
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={handleAcceptInvitation}
                startIcon={<PersonAddIcon />}
              >
                Accept Invitation
              </Button>
            </Box>
          )}

          {/* Accepting */}
          {pageState === 'accepting' && (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>Accepting invitation...</Typography>
            </Box>
          )}

          {/* Login Required */}
          {pageState === 'login_required' && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 3 }}>
                An account exists for <strong>{invitation?.email}</strong>. Please sign in to accept
                this invitation.
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/auth')}
                startIcon={<LoginIcon />}
              >
                Sign In
              </Button>
            </Box>
          )}

          {/* Signup Form */}
          {pageState === 'signup' && invitation && (
            <InvitationSignupForm
              email={invitation.email}
              organizationId={invitation.organizationId}
              organizationName={organizationName}
              role={invitation.role}
              employeeId={invitation.employeeId}
              invitationId={invitation.id}
              onComplete={handleSignupComplete}
            />
          )}
        </Paper>
      </Box>
    </Container>
  );
}
